import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'

import {
  aanmakenEmballage, aanmakenOverig, aanmakenVerpakking,
  bijwerkenEmballage, bijwerkenOverig, bijwerkenVerpakking,
  fetchEmballage, fetchOverig, fetchVerpakking,
  verwijderenEmballage, verwijderenOverig, verwijderenVerpakking,
  type Emballage, type EmballagePayload,
  type OverigActief, type OverigPayload,
  type Verpakking, type VerpakkingPayload,
} from '@/api/materiaal'
import { KpiCard } from '@/components/KpiCard'
import { Tabs } from '@/components/Tabs'
import { useCurrentOrg, useCurrentOrgId } from '@/hooks/useOrganisations'
import { useOrgLijsten } from '@/hooks/useOrgLijsten'
import { euro, nlGeheel } from '@/lib/format'

export function InvoerMateriaalPage() {
  const orgId = useCurrentOrgId()
  const org = useCurrentOrg()
  if (!orgId) return <Hdr><Box>Selecteer een klant in de zijbalk.</Box></Hdr>
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-kh-blue">Invoer verpakkingen, emballage &amp; overig</h1>
        <p className="text-sm text-kh-gray-500 mt-1">{org?.naam}</p>
      </div>
      <Tabs tabs={[
        { key: 'emb', label: 'Emballage',     content: <EmballageTab orgId={orgId} /> },
        { key: 'vp',  label: 'Verpakkingen',  content: <VerpakkingTab orgId={orgId} /> },
        { key: 'ov',  label: 'Overig',        content: <OverigTab orgId={orgId} /> },
      ]} />
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// Emballage
// ───────────────────────────────────────────────────────────────
function EmballageTab({ orgId }: { orgId: number }) {
  const qc = useQueryClient()
  const [zoek, setZoek] = useState('')
  const [selected, setSelected] = useState<Emballage | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const { data: lijsten } = useOrgLijsten(orgId)
  const { data, isLoading } = useQuery({
    queryKey: ['emballage', orgId, zoek],
    queryFn: () => fetchEmballage(orgId, zoek || undefined),
  })
  const totaal = (data ?? []).reduce((s, e) => s + (e.totale_waarde || 0), 0)

  const create = useMutation({
    mutationFn: (d: EmballagePayload) => aanmakenEmballage(orgId, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['emballage'] }); setShowAdd(false) },
  })
  const update = useMutation({
    mutationFn: (d: EmballagePayload) => bijwerkenEmballage(orgId, selected!.id, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['emballage'] }); setSelected(null) },
  })
  const del = useMutation({
    mutationFn: (id: number) => verwijderenEmballage(orgId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emballage'] }),
  })

  return (
    <div className="space-y-4">
      <KpiCard label="Totale emballagewaarde" value={euro(totaal)} />

      <div className="flex gap-2">
        <input className="input-field flex-1" placeholder="🔍 Zoek (type, artikelnr, locatie)"
               value={zoek} onChange={(e) => setZoek(e.target.value)} />
        <button className="btn-primary" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? 'Annuleren' : '+ Emballage toevoegen'}
        </button>
      </div>

      {showAdd && (
        <EmbForm initial={leegEmb()} onSubmit={(d) => create.mutate(d)} submitLabel="Toevoegen" lijsten={lijsten} />
      )}

      {isLoading ? <Box>Laden…</Box>
       : !data || data.length === 0 ? <Box>Nog geen emballage.</Box>
       : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <Thead cols={['Type', 'Artikelnr', 'Aantal', 'Waarde/stuk', 'Totaal', 'Locatie', 'Factuurnr.', 'Cred. nr.', '']} />
            <tbody className="divide-y divide-kh-gray-100">
              {data.map((e) => (
                <tr key={e.id} className={selected?.id === e.id ? 'bg-yellow-50' : ''}>
                  <td className="px-4 py-2 font-medium">{e.type_emballage}</td>
                  <td className="px-4 py-2 text-xs font-mono">{e.artikelnummer ?? ''}</td>
                  <td className="px-4 py-2 text-right">{nlGeheel(e.aantal)}</td>
                  <td className="px-4 py-2 text-right">{euro(e.waarde_per_stuk)}</td>
                  <td className="px-4 py-2 text-right font-medium">{euro(e.totale_waarde)}</td>
                  <td className="px-4 py-2 text-xs">{e.opslaglocatie ?? ''}</td>
                  <td className="px-4 py-2 text-xs font-mono">{e.inkoopfactuurnr ?? ''}</td>
                  <td className="px-4 py-2 text-xs">{e.crediteurnummer ?? ''}</td>
                  <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                    <button className="text-xs text-kh-blue hover:underline" onClick={() => setSelected(e)}>bewerken</button>
                    <button className="text-red-600 hover:text-red-800"
                            onClick={() => confirm(`'${e.type_emballage}' verwijderen?`) && del.mutate(e.id)}>
                      <Trash2 size={14} className="inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}

      {selected && (
        <div className="card p-5">
          <h3 className="font-semibold text-kh-blue mb-3">Bewerken: {selected.type_emballage}</h3>
          <EmbForm
            initial={{
              type_emballage: selected.type_emballage, artikelnummer: selected.artikelnummer,
              aantal: selected.aantal, waarde_per_stuk: selected.waarde_per_stuk, totale_waarde: null,
              opslaglocatie: selected.opslaglocatie, inkoopfactuurnr: selected.inkoopfactuurnr,
              crediteurnummer: selected.crediteurnummer, opmerkingen: selected.opmerkingen,
            }}
            onSubmit={(d) => update.mutate(d)} submitLabel="Opslaan" lijsten={lijsten}
            onCancel={() => setSelected(null)}
          />
        </div>
      )}
    </div>
  )
}

function leegEmb(): EmballagePayload {
  return { type_emballage: '', artikelnummer: '', aantal: 0, waarde_per_stuk: 0,
           totale_waarde: null, opslaglocatie: '', inkoopfactuurnr: '', crediteurnummer: '', opmerkingen: '' }
}

function EmbForm({ initial, onSubmit, submitLabel, lijsten, onCancel }: {
  initial: EmballagePayload; onSubmit: (d: EmballagePayload) => void; submitLabel: string;
  lijsten?: Record<string, string[]>; onCancel?: () => void
}) {
  const [f, setF] = useState(initial)
  return (
    <div className="card p-5 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Type emballage *">
          <input className="input-field" value={f.type_emballage}
                 onChange={(e) => setF({ ...f, type_emballage: e.target.value })} />
        </FormField>
        <FormField label="Artikelnummer">
          <input className="input-field" value={f.artikelnummer ?? ''}
                 onChange={(e) => setF({ ...f, artikelnummer: e.target.value })} />
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField label="Aantal *">
          <input className="input-field" type="number" value={f.aantal ?? 0}
                 onChange={(e) => setF({ ...f, aantal: Number(e.target.value) })} />
        </FormField>
        <FormField label="Waarde per stuk (€) *">
          <input className="input-field" type="number" step="0.01" value={f.waarde_per_stuk ?? 0}
                 onChange={(e) => setF({ ...f, waarde_per_stuk: Number(e.target.value) })} />
        </FormField>
        <FormField label="Opslaglocatie">
          <select className="input-field" value={f.opslaglocatie ?? ''}
                  onChange={(e) => setF({ ...f, opslaglocatie: e.target.value })}>
            <option value="">— selecteer —</option>
            {(lijsten?.['Opslaglocatie'] ?? []).map((o) => <option key={o}>{o}</option>)}
            <option value="Uitstaand bij debiteuren">Uitstaand bij debiteuren</option>
          </select>
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Inkoopfactuurnr.">
          <input className="input-field" value={f.inkoopfactuurnr ?? ''}
                 onChange={(e) => setF({ ...f, inkoopfactuurnr: e.target.value })} />
        </FormField>
        <FormField label="Crediteurnummer">
          <input className="input-field" value={f.crediteurnummer ?? ''}
                 onChange={(e) => setF({ ...f, crediteurnummer: e.target.value })} />
        </FormField>
      </div>
      <FormField label="Opmerkingen">
        <input className="input-field" value={f.opmerkingen ?? ''}
               onChange={(e) => setF({ ...f, opmerkingen: e.target.value })} />
      </FormField>
      <div className="flex gap-2">
        <button className="btn-primary" disabled={!f.type_emballage || !f.aantal || !f.waarde_per_stuk}
                onClick={() => onSubmit(f)}>{submitLabel}</button>
        {onCancel && <button className="btn-secondary" onClick={onCancel}>Annuleren</button>}
      </div>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// Verpakkingen
// ───────────────────────────────────────────────────────────────
function VerpakkingTab({ orgId }: { orgId: number }) {
  const qc = useQueryClient()
  const [zoek, setZoek] = useState('')
  const [selected, setSelected] = useState<Verpakking | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const { data: lijsten } = useOrgLijsten(orgId)
  const { data, isLoading } = useQuery({
    queryKey: ['verpakking', orgId, zoek],
    queryFn: () => fetchVerpakking(orgId, zoek || undefined),
  })
  const totaal = (data ?? []).reduce((s, v) => s + (v.totale_waarde || 0), 0)
  const create = useMutation({ mutationFn: (d: VerpakkingPayload) => aanmakenVerpakking(orgId, d),
                               onSuccess: () => { qc.invalidateQueries({ queryKey: ['verpakking'] }); setShowAdd(false) } })
  const update = useMutation({ mutationFn: (d: VerpakkingPayload) => bijwerkenVerpakking(orgId, selected!.id, d),
                               onSuccess: () => { qc.invalidateQueries({ queryKey: ['verpakking'] }); setSelected(null) } })
  const del = useMutation({ mutationFn: (id: number) => verwijderenVerpakking(orgId, id),
                            onSuccess: () => qc.invalidateQueries({ queryKey: ['verpakking'] }) })

  return (
    <div className="space-y-4">
      <KpiCard label="Totale waarde verpakkingsmateriaal" value={euro(totaal)} />
      <div className="flex gap-2">
        <input className="input-field flex-1" placeholder="🔍 Zoek (artikel, artikelnr, crediteur)"
               value={zoek} onChange={(e) => setZoek(e.target.value)} />
        <button className="btn-primary" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? 'Annuleren' : '+ Verpakkingsmateriaal toevoegen'}
        </button>
      </div>

      {showAdd && <VpForm initial={leegVp()} onSubmit={(d) => create.mutate(d)} submitLabel="Toevoegen" lijsten={lijsten} />}

      {isLoading ? <Box>Laden…</Box>
       : !data || data.length === 0 ? <Box>Nog geen verpakkingsmateriaal.</Box>
       : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <Thead cols={['Artikel', 'Artikelnr', 'Aantal', 'Eenheid', 'Prijs/eenheid', 'Totaal', 'Crediteur', 'Locatie', '']} />
            <tbody className="divide-y divide-kh-gray-100">
              {data.map((v) => (
                <tr key={v.id} className={selected?.id === v.id ? 'bg-yellow-50' : ''}>
                  <td className="px-4 py-2 font-medium">{v.artikel}</td>
                  <td className="px-4 py-2 text-xs font-mono">{v.artikelnummer ?? ''}</td>
                  <td className="px-4 py-2 text-right">{nlGeheel(v.aantal)}</td>
                  <td className="px-4 py-2">{v.eenheid ?? ''}</td>
                  <td className="px-4 py-2 text-right">{euro(v.inkoopprijs_per_eenheid)}</td>
                  <td className="px-4 py-2 text-right font-medium">{euro(v.totale_waarde)}</td>
                  <td className="px-4 py-2 text-xs">{v.crediteur ?? ''}</td>
                  <td className="px-4 py-2 text-xs">{v.opslaglocatie ?? ''}</td>
                  <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                    <button className="text-xs text-kh-blue hover:underline" onClick={() => setSelected(v)}>bewerken</button>
                    <button className="text-red-600 hover:text-red-800"
                            onClick={() => confirm(`'${v.artikel}' verwijderen?`) && del.mutate(v.id)}>
                      <Trash2 size={14} className="inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}

      {selected && (
        <div className="card p-5">
          <h3 className="font-semibold text-kh-blue mb-3">Bewerken: {selected.artikel}</h3>
          <VpForm
            initial={{
              artikel: selected.artikel, artikelnummer: selected.artikelnummer,
              aantal: selected.aantal, eenheid: selected.eenheid,
              inkoopprijs_per_eenheid: selected.inkoopprijs_per_eenheid, totale_waarde: null,
              minimum_voorraad: selected.minimum_voorraad, status: selected.status,
              crediteur: selected.crediteur, opslaglocatie: selected.opslaglocatie,
              inkoopfactuurnr: selected.inkoopfactuurnr, crediteurnummer: selected.crediteurnummer,
              opmerkingen: selected.opmerkingen,
            }}
            onSubmit={(d) => update.mutate(d)} submitLabel="Opslaan" lijsten={lijsten}
            onCancel={() => setSelected(null)}
          />
        </div>
      )}
    </div>
  )
}

function leegVp(): VerpakkingPayload {
  return { artikel: '', artikelnummer: '', aantal: 0, eenheid: '',
           inkoopprijs_per_eenheid: 0, totale_waarde: null,
           minimum_voorraad: null, status: 'OK', crediteur: '', opslaglocatie: '',
           inkoopfactuurnr: '', crediteurnummer: '', opmerkingen: '' }
}

function VpForm({ initial, onSubmit, submitLabel, lijsten, onCancel }: {
  initial: VerpakkingPayload; onSubmit: (d: VerpakkingPayload) => void; submitLabel: string;
  lijsten?: Record<string, string[]>; onCancel?: () => void
}) {
  const [f, setF] = useState(initial)
  return (
    <div className="card p-5 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Artikel *">
          <input className="input-field" value={f.artikel}
                 onChange={(e) => setF({ ...f, artikel: e.target.value })} />
        </FormField>
        <FormField label="Artikelnummer">
          <input className="input-field" value={f.artikelnummer ?? ''}
                 onChange={(e) => setF({ ...f, artikelnummer: e.target.value })} />
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField label="Aantal *">
          <input className="input-field" type="number" step="0.01" value={f.aantal ?? 0}
                 onChange={(e) => setF({ ...f, aantal: Number(e.target.value) })} />
        </FormField>
        <FormField label="Eenheid">
          <select className="input-field" value={f.eenheid ?? ''}
                  onChange={(e) => setF({ ...f, eenheid: e.target.value })}>
            <option value="">— selecteer —</option>
            {(lijsten?.['Eenheid'] ?? []).map((o) => <option key={o}>{o}</option>)}
          </select>
        </FormField>
        <FormField label="Inkoopprijs per eenheid (€)">
          <input className="input-field" type="number" step="0.001" value={f.inkoopprijs_per_eenheid ?? 0}
                 onChange={(e) => setF({ ...f, inkoopprijs_per_eenheid: Number(e.target.value) })} />
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField label="Minimum voorraad">
          <input className="input-field" type="number" value={f.minimum_voorraad ?? 0}
                 onChange={(e) => setF({ ...f, minimum_voorraad: Number(e.target.value) || null })} />
        </FormField>
        <FormField label="Crediteur">
          <input className="input-field" list="cred-opts" value={f.crediteur ?? ''}
                 onChange={(e) => setF({ ...f, crediteur: e.target.value })} />
          <datalist id="cred-opts">
            {(lijsten?.['Crediteur'] ?? []).map((o) => <option key={o} value={o} />)}
          </datalist>
        </FormField>
        <FormField label="Opslaglocatie">
          <select className="input-field" value={f.opslaglocatie ?? ''}
                  onChange={(e) => setF({ ...f, opslaglocatie: e.target.value })}>
            <option value="">— selecteer —</option>
            {(lijsten?.['Opslaglocatie'] ?? []).map((o) => <option key={o}>{o}</option>)}
          </select>
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Inkoopfactuurnr.">
          <input className="input-field" value={f.inkoopfactuurnr ?? ''}
                 onChange={(e) => setF({ ...f, inkoopfactuurnr: e.target.value })} />
        </FormField>
        <FormField label="Crediteurnummer">
          <input className="input-field" value={f.crediteurnummer ?? ''}
                 onChange={(e) => setF({ ...f, crediteurnummer: e.target.value })} />
        </FormField>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary" disabled={!f.artikel || !f.aantal} onClick={() => onSubmit(f)}>{submitLabel}</button>
        {onCancel && <button className="btn-secondary" onClick={onCancel}>Annuleren</button>}
      </div>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────
// Overig
// ───────────────────────────────────────────────────────────────
function OverigTab({ orgId }: { orgId: number }) {
  const qc = useQueryClient()
  const [zoek, setZoek] = useState('')
  const [selected, setSelected] = useState<OverigActief | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const { data: lijsten } = useOrgLijsten(orgId)
  const { data, isLoading } = useQuery({
    queryKey: ['overig', orgId, zoek],
    queryFn: () => fetchOverig(orgId, zoek || undefined),
  })
  const totaal = (data ?? []).reduce((s, o) => s + (o.totale_waarde || 0), 0)
  const create = useMutation({ mutationFn: (d: OverigPayload) => aanmakenOverig(orgId, d),
                               onSuccess: () => { qc.invalidateQueries({ queryKey: ['overig'] }); setShowAdd(false) } })
  const update = useMutation({ mutationFn: (d: OverigPayload) => bijwerkenOverig(orgId, selected!.id, d),
                               onSuccess: () => { qc.invalidateQueries({ queryKey: ['overig'] }); setSelected(null) } })
  const del = useMutation({ mutationFn: (id: number) => verwijderenOverig(orgId, id),
                            onSuccess: () => qc.invalidateQueries({ queryKey: ['overig'] }) })

  return (
    <div className="space-y-4">
      <KpiCard label="Totale waarde overige financierbare activa" value={euro(totaal)} />
      <p className="text-sm text-kh-gray-500">
        💡 Voer hier overige activa in die meefinancieren maar geen voorraad, emballage of verpakking zijn.
      </p>
      <div className="flex gap-2">
        <input className="input-field flex-1" placeholder="🔍 Zoek (omschrijving, categorie)"
               value={zoek} onChange={(e) => setZoek(e.target.value)} />
        <button className="btn-primary" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? 'Annuleren' : '+ Overig actief toevoegen'}
        </button>
      </div>

      {showAdd && <OvForm initial={leegOv()} onSubmit={(d) => create.mutate(d)} submitLabel="Toevoegen" lijsten={lijsten} />}

      {isLoading ? <Box>Laden…</Box>
       : !data || data.length === 0 ? <Box>Nog geen overige activa.</Box>
       : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <Thead cols={['Omschrijving', 'Categorie', 'Aantal', 'Eenheid', 'Prijs/eenheid', 'Totaal', 'Factuurnr.', '']} />
            <tbody className="divide-y divide-kh-gray-100">
              {data.map((o) => (
                <tr key={o.id} className={selected?.id === o.id ? 'bg-yellow-50' : ''}>
                  <td className="px-4 py-2 font-medium">{o.omschrijving}</td>
                  <td className="px-4 py-2">{o.categorie ?? ''}</td>
                  <td className="px-4 py-2 text-right">{nlGeheel(o.aantal)}</td>
                  <td className="px-4 py-2">{o.eenheid ?? ''}</td>
                  <td className="px-4 py-2 text-right">{euro(o.waarderingsprijs_per_eenheid)}</td>
                  <td className="px-4 py-2 text-right font-medium">{euro(o.totale_waarde)}</td>
                  <td className="px-4 py-2 text-xs font-mono">{o.inkoopfactuurnr ?? ''}</td>
                  <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                    <button className="text-xs text-kh-blue hover:underline" onClick={() => setSelected(o)}>bewerken</button>
                    <button className="text-red-600 hover:text-red-800"
                            onClick={() => confirm(`'${o.omschrijving}' verwijderen?`) && del.mutate(o.id)}>
                      <Trash2 size={14} className="inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}

      {selected && (
        <div className="card p-5">
          <h3 className="font-semibold text-kh-blue mb-3">Bewerken: {selected.omschrijving}</h3>
          <OvForm
            initial={{
              omschrijving: selected.omschrijving, categorie: selected.categorie,
              aantal: selected.aantal, eenheid: selected.eenheid,
              waarderingsprijs_per_eenheid: selected.waarderingsprijs_per_eenheid, totale_waarde: null,
              inkoopfactuurnr: selected.inkoopfactuurnr, crediteurnummer: selected.crediteurnummer,
              inslagdatum: selected.inslagdatum, opmerkingen: selected.opmerkingen,
            }}
            onSubmit={(d) => update.mutate(d)} submitLabel="Opslaan" lijsten={lijsten}
            onCancel={() => setSelected(null)}
          />
        </div>
      )}
    </div>
  )
}

function leegOv(): OverigPayload {
  return { omschrijving: '', categorie: '', aantal: 0, eenheid: '',
           waarderingsprijs_per_eenheid: 0, totale_waarde: null,
           inkoopfactuurnr: '', crediteurnummer: '',
           inslagdatum: new Date().toISOString().slice(0, 10), opmerkingen: '' }
}

function OvForm({ initial, onSubmit, submitLabel, lijsten, onCancel }: {
  initial: OverigPayload; onSubmit: (d: OverigPayload) => void; submitLabel: string;
  lijsten?: Record<string, string[]>; onCancel?: () => void
}) {
  const [f, setF] = useState(initial)
  return (
    <div className="card p-5 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Omschrijving *">
          <input className="input-field" value={f.omschrijving}
                 onChange={(e) => setF({ ...f, omschrijving: e.target.value })} />
        </FormField>
        <FormField label="Categorie">
          <select className="input-field" value={f.categorie ?? ''}
                  onChange={(e) => setF({ ...f, categorie: e.target.value })}>
            <option value="">— selecteer —</option>
            {(lijsten?.['Overig_categorie'] ?? []).map((o) => <option key={o}>{o}</option>)}
          </select>
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField label="Aantal">
          <input className="input-field" type="number" step="0.01" value={f.aantal ?? 0}
                 onChange={(e) => setF({ ...f, aantal: Number(e.target.value) })} />
        </FormField>
        <FormField label="Eenheid">
          <select className="input-field" value={f.eenheid ?? ''}
                  onChange={(e) => setF({ ...f, eenheid: e.target.value })}>
            <option value="">— selecteer —</option>
            {(lijsten?.['Eenheid'] ?? []).map((o) => <option key={o}>{o}</option>)}
          </select>
        </FormField>
        <FormField label="Waarderingsprijs per eenheid (€) *">
          <input className="input-field" type="number" step="0.01" value={f.waarderingsprijs_per_eenheid ?? 0}
                 onChange={(e) => setF({ ...f, waarderingsprijs_per_eenheid: Number(e.target.value) })} />
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField label="Inkoopfactuurnr.">
          <input className="input-field" value={f.inkoopfactuurnr ?? ''}
                 onChange={(e) => setF({ ...f, inkoopfactuurnr: e.target.value })} />
        </FormField>
        <FormField label="Crediteurnummer">
          <input className="input-field" value={f.crediteurnummer ?? ''}
                 onChange={(e) => setF({ ...f, crediteurnummer: e.target.value })} />
        </FormField>
        <FormField label="Inslagdatum">
          <input className="input-field" type="date" value={f.inslagdatum ?? ''}
                 onChange={(e) => setF({ ...f, inslagdatum: e.target.value || null })} />
        </FormField>
      </div>
      <FormField label="Opmerkingen">
        <textarea className="input-field" rows={2} value={f.opmerkingen ?? ''}
                  onChange={(e) => setF({ ...f, opmerkingen: e.target.value })} />
      </FormField>
      <div className="flex gap-2">
        <button className="btn-primary"
                disabled={!f.omschrijving || !f.waarderingsprijs_per_eenheid}
                onClick={() => onSubmit(f)}>{submitLabel}</button>
        {onCancel && <button className="btn-secondary" onClick={onCancel}>Annuleren</button>}
      </div>
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────
function Hdr({ children }: { children: React.ReactNode }) {
  return <div><div className="mb-6"><h1 className="text-2xl font-semibold text-kh-blue">Invoer materiaal</h1></div>{children}</div>
}
function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block text-sm"><span className="block font-medium text-kh-gray-700 mb-1">{label}</span>{children}</label>
}
function Thead({ cols }: { cols: string[] }) {
  return <thead className="bg-kh-gray-50 text-kh-gray-600 uppercase text-xs tracking-wider"><tr>{cols.map((c, i) => <th key={i} className="text-left px-4 py-3 whitespace-nowrap">{c}</th>)}</tr></thead>
}
function Box({ children }: { children: React.ReactNode }) {
  return <div className="card p-6 text-center text-kh-gray-500">{children}</div>
}
